import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { clone, isEmpty } from 'ramda'
import { Element, scroller } from 'react-scroll'
import toPath from 'lodash/toPath'

import * as translationService from '../../services/translationService'

import CardSelector from '../../components/CardSelector'
import { TextField, Option } from 'components/Form'
import {
  Stack,
  TextContainer,
  DisplayText,
  Icon,
  Button,
  Modal,
  ModalBody,
  CurrencyField,
  Markdown,
  MobileNumber,
  Notice,
  ErrorText,
  PlaceAutocomplete,
  AddressField,
  OptionList,
} from './../../components'
import SceneComponent from './SceneComponent'
import { updateApplicationInformation, softUpdateApplicationInformation } from '../../actions/FlowAction'
import { validateIncome } from '../../utils/validation'
import { getRandomString } from '../../utils/generator'
import { getIsIncomeRequired, getIsFirstPhase, getShouldIncludeIncomeStatement } from '../../selectors/flowSelector'
import { FINANCIAL_REMARKS_MAX_LENGTH } from '../../utils/validation/constants'

const types = [
  {
    id: 'INTAKE_INCOME_SALARY',
    icon: <Icon source="salary" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_PENSION',
    icon: <Icon source="pension" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_DOLE',
    icon: <Icon source="dole" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_ALIMONY',
    icon: <Icon source="alimony" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_CHILDBENEFIT',
    icon: <Icon source="childBenefit" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_INTERESTS',
    icon: <Icon source="interests" />,
    min: 0,
  },
  {
    id: 'INTAKE_INCOME_OTHER',
    icon: <Icon source="income" />,
    min: 0,
  },
]

const defaultPerson = {
  uid: getRandomString(),
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  documents: [],
  income: [],
  hasReleventIncome: true,
  address: {
    number: '',
    street: '',
    city: '',
    zip: '',
    countryId: '',
  },
}

class FinancialScene extends SceneComponent {
  static propTypes = {
    application: PropTypes.any,
    step: PropTypes.any,
  }
  state = {
    infoModalIsOpen: false,
    contacts: [],
    financialRemarks: '',
  }

  mainError = React.createRef()

  componentDidMount(...args) {
    const {
      inModal,
      application: { contacts, financialRemarks, inGoodConscience, acceptDataUsages, incomeStatement },
      isFirstPhase,
      isIncomeRequired,
      shouldIncludeIncomeStatement,
    } = this.props

    super.componentDidMount.apply(this, args)
    if (this.props.checkForError) {
      return validateIncome({
        contacts,
        inModal,
        financialRemarks,
        inGoodConscience,
        acceptDataUsages,
        isFirstPhase,
        isIncomeRequired,
        shouldIncludeIncomeStatement,
        incomeStatement,
      }).catch(fieldErrors => {
        console.warn('FinancialScene, componentDidMount', { fieldErrors })
        this.setState(
          {
            fieldErrors,
          },
          () => {
            scroller.scrollTo('mainError', {
              smooth: true,
              offset: -40, // Scrolls to element - 40 pixels down the page
              containerId: this.props.inModal && 'sceneModal',
            })
          },
        )
      })
    }
  }

  componentDidUpdate(prevProps) {
    const { contacts } = this.props.application
    // Typical usage (don't forget to compare props):
    if (this.props.application.contacts !== prevProps.application.contacts) {
      contacts.map(contact => {
        if (!contact.uid) contact.uid = getRandomString()
        if (!contact.income) contact.income = []
        return contact
      })
      this.props.softUpdateApplicationInformation({ contacts })
    }
  }

  toggleModal = () => {
    this.setState(prevState => {
      return { infoModalIsOpen: !prevState.infoModalIsOpen }
    })
  }

  addPerson = event => {
    const { contacts } = this.props.application
    event.preventDefault()
    contacts.push(clone(defaultPerson))
    this.props.softUpdateApplicationInformation({ contacts })
  }

  removePerson = (event, index) => {
    const { contacts } = this.props.application
    event.preventDefault()
    contacts.splice(index, 1)
    this.props.softUpdateApplicationInformation({ contacts })
  }

  onSubmit = async (shouldValidate = true) => {
    const {
      step,
      inModal,
      application: { contacts, financialRemarks, inGoodConscience, finishedStep, acceptDataUsages, incomeStatement },
      isFirstPhase,
      isIncomeRequired,
      shouldIncludeIncomeStatement,
    } = this.props

    if (shouldValidate) {
      try {
        await validateIncome({
          contacts,
          inModal,
          financialRemarks,
          inGoodConscience,
          isFirstPhase,
          acceptDataUsages,
          isIncomeRequired,
          shouldIncludeIncomeStatement,
          incomeStatement,
        })

        if (isFirstPhase) {
          return this.props.updateApplicationInformation({
            contacts,
            financialRemarks,
            inGoodConscience,
            incomeStatement,
            finishedStep: { ...finishedStep, [step]: true },
          })
        } else {
          return this.props.updateApplicationInformation({
            contacts,
            financialRemarks,
            inGoodConscience,
            acceptDataUsages,
          })
        }
      } catch (fieldErrors) {
        this.setState(
          {
            fieldErrors,
          },
          () => {
            scroller.scrollTo('mainError', {
              smooth: true,
              offset: -40, // Scrolls to element - 40 pixels down the page
              containerId: this.props.inModal && 'sceneModal',
            })
          },
        )
        throw fieldErrors
      }
    } else {
      return this.props.updateApplicationInformation({ contacts, financialRemarks, inGoodConscience, acceptDataUsages })
    }
  }

  resetErrors = () => {
    this.setState({
      fieldErrors: {},
    })
  }

  handleIncomeAdd = (typeIds, personIndex) => {
    const { contacts } = this.props.application
    const { income = [] } = contacts[personIndex]

    typeIds.forEach(typeId => {
      const type = types.find(type => type.id === typeId)
      income.push({
        id: type.id,
        value: type.min,
        documents: [],
        handOff: false,
      })
    })

    this.props.softUpdateApplicationInformation({ contacts })
    this.setState({
      fieldErrors: {},
    })
  }

  handleIncomeRemove = (typeId, personIndex) => {
    const { contacts } = this.props.application
    const { income } = contacts[personIndex]
    const newIncome = income.filter(item => item.id !== typeId)
    contacts[personIndex].income = newIncome
    this.props.softUpdateApplicationInformation({ contacts })
  }

  handleIncomeChange = (typeId, newValue, personIndex) => {
    const { contacts } = this.props.application
    const { income } = contacts[personIndex]
    income.map(income => {
      if (income.id === typeId) income.value = newValue
      return income
    })
    this.props.softUpdateApplicationInformation({ contacts })
  }

  // onCardSelectorChange = (event, personIndex) => {
  //   const { contacts } = this.state
  //   const { target = {}, targets = {}, change } = event

  //   if (change === 'removed') {
  //     delete contacts[personIndex].income[target.id]
  //   } else {
  //     contacts[personIndex].income = {
  //       ...contacts[personIndex].income,
  //       ...target,
  //       ...targets,
  //     }
  //   }
  //   this.setState({
  //     contacts,
  //   })
  // }

  onChange = ({ target: { name, value } }, personIndex) => {
    const { contacts } = this.props.application
    contacts[personIndex][name] = value
    this.props.softUpdateApplicationInformation({ contacts })
  }

  onCheckboxChange = ({ target: { checked, name } }, personIndex) => {
    this.onChange({ target: { name, value: checked } }, personIndex)
  }

  handleCheckFeasibilityChange = personIndex => ({ target: { checked, name } }) => {
    this.onChange({ target: { name, value: checked } }, personIndex)
  }

  handleGoogleAddressSelect = (address, personIndex) => {
    const { contacts } = this.props.application
    contacts[personIndex].address = address
    this.props.softUpdateApplicationInformation({ contacts })
  }

  handleAddressChange = ({ target: { value, name } }, personIndex) => {
    const { contacts } = this.props.application
    const addressItemKey = toPath(name)[1]
    const newContacts = contacts.map((contact, index) => {
      return personIndex === index
        ? {
            ...contact,
            address: {
              ...contact.address,
              [addressItemKey]: value,
            },
          }
        : contact
    })
    this.props.softUpdateApplicationInformation({ contacts: newContacts })
  }

  handleConscienceChange = ({ target: { checked } }) => {
    let { inGoodConscience } = this.props.application
    inGoodConscience = checked
    this.props.softUpdateApplicationInformation({ inGoodConscience })
  }

  handleTextFieldFocus = ({ target }) => {
    target.select()
  }
  handleTextFieldMouseUp = event => {
    event.preventDefault()
  }

  handleRemarksChange = ({ target: { id, value } }) => {
    this.props.softUpdateApplicationInformation({
      [id]: value,
    })
  }

  handleAcceptDataUsages = ({ target: { checked } }) => {
    this.props.softUpdateApplicationInformation({ acceptDataUsages: checked })
  }

  handleIncomeStatementChange = (value, name) => {
    this.props.softUpdateApplicationInformation({ [name]: value, inGoodConscience: true })
    this.resetErrors()
  }

  render() {
    const { fieldErrors = {} } = this.state
    const { inModal, isFirstPhase, isIncomeRequired, shouldIncludeIncomeStatement } = this.props
    const { contacts, financialRemarks, inGoodConscience, incomeStatement } = this.props.application

    const financialRemarksCharactersLeft = FINANCIAL_REMARKS_MAX_LENGTH - (financialRemarks || '').length
    return (
      <div>
        <TextContainer>
          {!inModal && (
            <DisplayText>
              {translationService.financialTitle()}{' '}
              {!isIncomeRequired && <small>{translationService.t('system.optional')}</small>}
            </DisplayText>
          )}
          {!isEmpty(fieldErrors) && (
            <Element name="mainError">
              <Notice type="error">{translationService.intakeFormErrors()}</Notice>
            </Element>
          )}
          <p>
            {translationService.financialIntro()}
            <br />
            <Button onClick={this.toggleModal} plain>
              {translationService.financialMoreInfoAction()}
            </Button>
          </p>
          <div>
            {contacts.map((person, index) => {
              const errors = fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index]
              return (
                <TextContainer key={index} asCard={true}>
                  {index === 0 ? (
                    <DisplayText size="small">
                      {person.firstname} {person.lastname}
                    </DisplayText>
                  ) : (
                    <React.Fragment>
                      <Stack distribution="equalSpacing">
                        <DisplayText size="small">
                          {person.firstname || person.lastname
                            ? `${person.firstname} ${person.lastname}`
                            : `${translationService.t('system.tenant')} ${index + 1}`}
                        </DisplayText>
                        <Button plain onClick={event => this.removePerson(event, index)}>
                          <Icon source="bin" />
                        </Button>
                      </Stack>
                      <Option
                        type="checkbox"
                        label={translationService.t('intake.person.is.guarantor')}
                        id={`isGuarantor-${index}`}
                        name="isGuarantor"
                        checked={person.isGuarantor}
                        onChange={event => this.onCheckboxChange(event, index)}
                      />
                      <div>
                        <Stack distribution="fill">
                          <TextField
                            error={
                              fieldErrors.contacts &&
                              fieldErrors.contacts[index] &&
                              fieldErrors.contacts[index].firstname
                            }
                            label={translationService.t('system.firstname')}
                            id={`firstname-${index}`}
                            name="firstname"
                            value={person.firstname}
                            onChange={event => this.onChange(event, index)}
                          />
                          <TextField
                            error={
                              fieldErrors.contacts &&
                              fieldErrors.contacts[index] &&
                              fieldErrors.contacts[index].lastname
                            }
                            label={translationService.t('system.lastname')}
                            id={`lastname-${index}`}
                            name="lastname"
                            value={person.lastname}
                            onChange={event => this.onChange(event, index)}
                          />
                        </Stack>
                      </div>
                      <TextField
                        error={fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index].email}
                        label={translationService.t('system.email')}
                        id={`email-${index}`}
                        name="email"
                        value={person.email}
                        onChange={event => this.onChange(event, index)}
                      />
                      <MobileNumber
                        error={fieldErrors.contacts && fieldErrors.contacts[index] && fieldErrors.contacts[index].phone}
                        label={translationService.t('system.mobile_number')}
                        id={`phone-${index}`}
                        name="phone"
                        value={person.phone}
                        onChange={event => this.onChange(event, index)}
                      />
                      <PlaceAutocomplete onSelect={address => this.handleGoogleAddressSelect(address, index)} />
                      <AddressField
                        type="text"
                        name={`address-${index}`}
                        id={`address-${index}`}
                        address={{ ...person.address }}
                        onChange={event => this.handleAddressChange(event, index)}
                      />
                    </React.Fragment>
                  )}
                  <CardSelector
                    error={errors && errors.income}
                    id="types"
                    values={person.income}
                    onAdd={(...rest) => this.handleIncomeAdd(...rest, index)}
                    onRemove={(...rest) => this.handleIncomeRemove(...rest, index)}
                    onChange={(...rest) => this.handleIncomeChange(...rest, index)}
                    types={types}
                    title={translationService.t('intake.add.income.title')}
                    action={translationService.t('intake.add.income.action')}
                    field={
                      <CurrencyField onFocus={this.handleTextFieldFocus} onMouseUp={this.handleTextFieldMouseUp} />
                    }
                  />
                  {isFirstPhase && (
                    <Option
                      type="checkbox"
                      label={translationService.financialCheckFeasibilityLabel()}
                      id={`check-feasibility-${index}`}
                      name="checkFeasibility"
                      checked={person.checkFeasibility}
                      hasError={errors && errors.checkFeasibility}
                      onChange={this.handleCheckFeasibilityChange(index)}
                    />
                  )}
                </TextContainer>
              )
            })}
          </div>
          <div>
            <Button outline onClick={this.addPerson}>
              + {translationService.t('intake.add.tenant.guarantor')}
            </Button>
          </div>
          <DisplayText size="small">
            {translationService.extraRemarks()} <small>{translationService.t('system.optional')}</small>
          </DisplayText>
          <TextField
            multiline={true}
            id="financialRemarks"
            name="financialRemarks"
            value={financialRemarks}
            placeholder={translationService.financialExtraRemarksPlaceholder()}
            onChange={this.handleRemarksChange}
            maxLength={FINANCIAL_REMARKS_MAX_LENGTH}
          />
          <div
            style={{
              marginTop: 2,
              color: (() => {
                if (financialRemarksCharactersLeft <= 0) {
                  return 'red'
                }

                if (financialRemarksCharactersLeft < 100) {
                  return 'orange'
                }

                return 'gray'
              })(),
            }}>
            {financialRemarksCharactersLeft}/{FINANCIAL_REMARKS_MAX_LENGTH}
          </div>
          {isIncomeRequired && (
            <Option
              type="checkbox"
              label={translationService.financialInGoodConscience()}
              id="in-good-conscience"
              name="inGoodConscience"
              checked={inGoodConscience}
              hasError={fieldErrors && fieldErrors.inGoodConscience}
              onChange={this.handleConscienceChange}
            />
          )}
          {isFirstPhase &&
            shouldIncludeIncomeStatement && (
              <React.Fragment>
                <DisplayText size="small">
                  {translationService.financialIncomeStatementHeading()}{' '}
                  <Button onClick={this.toggleModal} plain>
                    {translationService.financialMoreInfoAction()}
                  </Button>
                </DisplayText>
                <OptionList
                  name="incomeStatement"
                  options={[
                    {
                      label: translationService.financialIncomeStatementOptionEqualOrMoreLabel(),
                      value: 'INTAKE_INCOME_STATEMENT_EQUAL_OR_MORE',
                    },
                    {
                      label: translationService.financialIncomeStatementOptionLessLabel(),
                      value: 'INTAKE_INCOME_STATEMENT_LESS',
                    },
                    {
                      label: translationService.financialIncomeStatementOptionRefuseLabel(),
                      value: 'INTAKE_INCOME_STATEMENT_REFUSE',
                    },
                  ]}
                  selected={incomeStatement}
                  error={fieldErrors && fieldErrors.incomeStatement}
                  onChange={this.handleIncomeStatementChange}
                />
              </React.Fragment>
            )}
          {fieldErrors && fieldErrors.inGoodConscience && <ErrorText transKey={fieldErrors.inGoodConscience} />}
        </TextContainer>
        <Modal
          isOpen={this.state.infoModalIsOpen}
          onRequestClose={this.toggleModal}
          title={translationService.financialMoreInfoTitle()}>
          <ModalBody>{<Markdown source={translationService.financialMoreInfoText()} />}</ModalBody>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = ({ FlowReducer }) => {
  const { application, step, property } = FlowReducer
  return {
    property,
    application,
    step,
    isFirstPhase: getIsFirstPhase(FlowReducer),
    isIncomeRequired: getIsIncomeRequired(FlowReducer),
    shouldIncludeIncomeStatement: getShouldIncludeIncomeStatement(FlowReducer),
  }
}
const mapDispatchToProps = dispatch => ({
  updateApplicationInformation: information => dispatch(updateApplicationInformation(information)),
  softUpdateApplicationInformation: information => dispatch(softUpdateApplicationInformation(information)),
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(FinancialScene)



// WEBPACK FOOTER //
// ./src/views/scenes/FinancialScene.js